import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { NotificationChannel, NotificationDelivery, NotificationDomain, NotificationEvent, NotificationPreferences, NotificationTopic } from '../types/notifications';
import { defaultPreferences, seedNotifications } from '../data/notifications';

/** Domains a preference cannot switch off. */
const ALWAYS: NotificationDomain[] = ['security', 'safety', 'money'];
export function isAlwaysSent(domain: NotificationDomain): boolean {
  return ALWAYS.includes(domain);
}

/**
 * Works out which channels an event actually goes over, from the person's
 * preferences. A suppressed channel is recorded as suppressed rather than
 * dropped, so the record shows what was decided and why.
 */
export function planDelivery(domain: NotificationDomain, prefs: NotificationPreferences): NotificationDelivery[] {
  const always = isAlwaysSent(domain);
  const topicOn = always ? true : prefs.topics[domain as NotificationTopic] !== false;
  const channels: NotificationChannel[] = ['inApp', 'email', 'push', 'sms'];
  return channels.map(channel => {
    // In-app is never switched off: the centre is the record of what happened.
    if (channel === 'inApp') return {
      channel,
      state: 'delivered'
    };
    if (!topicOn) {
      return {
        channel,
        state: 'suppressed',
        note: 'You have this topic switched off. It is still recorded here.'
      };
    }
    if (!prefs.channels[channel]) {
      return {
        channel,
        state: 'suppressed',
        note: 'This channel is switched off in Settings.'
      };
    }
    if (channel === 'push') {
      return {
        channel,
        state: 'not_configured',
        note: 'Push needs a Campuzon app installed on your device.'
      };
    }
    if (channel === 'sms' && !always) {
      return {
        channel,
        state: 'suppressed',
        note: 'SMS is reserved for safety, security and money because it costs to send.'
      };
    }
    return {
      channel,
      state: 'delivered'
    };
  });
}
interface NotificationContextValue {
  all: NotificationEvent[];
  /** Everything not archived, newest first, criticals ahead of the rest. */
  inbox: NotificationEvent[];
  archived: NotificationEvent[];
  unreadCount: number;
  unreadByDomain: Record<string, number>;
  preferences: NotificationPreferences;
  savePreferences: (next: NotificationPreferences) => void;
  markRead: (id: string) => void;
  markUnread: (id: string) => void;
  markAllRead: () => void;
  archive: (id: string) => void;
  restore: (id: string) => void;
  /** Raise a new event. Delivery is planned from current preferences. */
  notify: (event: Omit<NotificationEvent, 'id' | 'read' | 'archived' | 'deliveries' | 'minutesAgo' | 'time'>) => void;
}
const NotificationContext = createContext<NotificationContextValue | null>(null);
const PRIORITY_RANK = {
  critical: 0,
  high: 1,
  normal: 2,
  low: 3
} as const;
export function NotificationProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [all, setAll] = useState<NotificationEvent[]>(seedNotifications);
  const [preferences, setPreferences] = useState<NotificationPreferences>(defaultPreferences);
  const sort = useCallback((items: NotificationEvent[]) => [...items].sort((a, b) => {
    // Criticals first regardless of age: a safety alert outranks a newer
    // group reply. Everything else is newest first.
    const rank = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
    if (a.priority === 'critical' || b.priority === 'critical') {
      if (rank !== 0) return rank;
    }
    return a.minutesAgo - b.minutesAgo;
  }), []);
  const value = useMemo<NotificationContextValue>(() => {
    const live = all.filter(item => !item.archived);
    const unreadByDomain: Record<string, number> = {};
    for (const item of live) {
      if (!item.read) unreadByDomain[item.domain] = (unreadByDomain[item.domain] ?? 0) + 1;
    }
    return {
      all,
      inbox: sort(live),
      archived: sort(all.filter(item => item.archived)),
      unreadCount: live.filter(item => !item.read).length,
      unreadByDomain,
      preferences,
      savePreferences: setPreferences,
      markRead: id => setAll(current => current.map(item => item.id === id ? {
        ...item,
        read: true
      } : item)),
      markUnread: id => setAll(current => current.map(item => item.id === id ? {
        ...item,
        read: false
      } : item)),
      markAllRead: () => setAll(current => current.map(item => ({
        ...item,
        read: true
      }))),
      archive: id => setAll(current => current.map(item => item.id === id ? {
        ...item,
        archived: true,
        read: true
      } : item)),
      restore: id => setAll(current => current.map(item => item.id === id ? {
        ...item,
        archived: false
      } : item)),
      notify: event => setAll(current => [{
        ...event,
        id: `ntf-${Date.now()}`,
        read: false,
        archived: false,
        minutesAgo: 0,
        time: 'Just now',
        deliveries: planDelivery(event.domain, preferences)
      }, ...current])
    };
  }, [all, preferences, sort]);
  return <NotificationContext.Provider value={value} data-id="element-6074" data-mp="35fi9x71v4lp">{children}</NotificationContext.Provider>;
}
export function useNotifications(): NotificationContextValue {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used inside NotificationProvider');
  return context;
}